import { config } from "@/shared/lib/config";
import {
  fetchLookupForInfinite,
  getInitials,
  hydrateLookupItem,
  type LookupItem,
} from "@/shared/lib/utils";
import { resolveImageSrc } from "@/shared/ui/AppImage";
import { FormInfiniteCombobox } from "@/shared/ui/FormInfiniteCombobox";
import { InfiniteCombobox } from "@/shared/ui/InfiniteCombobox";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  cn,
  Field,
  FormInput,
  FormRoot,
  FormTextarea,
  RadioGroup,
  RadioGroupItem,
  RHF,
  toast,
  zodResolver,
} from "@devhop/ui";
import { useQuery } from "@tanstack/react-query";
import {
  CalendarIcon,
  GripVertical,
  PlusIcon,
  Sparkles,
  Trash2,
} from "lucide-react";
import React from "react";
import { useFieldArray } from "react-hook-form";
import { z } from "zod";

type UserLookupItem = LookupItem & { image?: string | null };

const LEVEL_OPTIONS = [
  { value: "organization", label: "ລະດັບອົງກອນ" },
  { value: "department", label: "ລະດັບຜະແນກ" },
  { value: "position", label: "ລະດັບຕຳແໜ່ງ" },
] as const;

const ProjectFormSchema = z
  .object({
    name: z.string().min(1, "ກະລຸນາປ້ອນຊື່ໂຄງການ"),
    description: z.string().optional(),
    startDate: z.string().min(1, "ກະລຸນາເລືອກວັນເລີ່ມ"),
    endDate: z.string().min(1, "ກະລຸນາເລືອກວັນສິ້ນສຸດ"),
    responsibleUserId: z.string().optional(),
    level: z.enum(["position", "department", "organization"]).optional(),
    positionId: z.string().optional().nullable(),
    departmentId: z.string().optional().nullable(),
    phases: z.array(
      z.object({
        id: z.string(),
        name: z.string().min(1, "ກະລຸນາປ້ອນຊື່ຂັ້ນຕອນ"),
        dueDate: z.string().optional(),
        assigneeId: z.string().optional(),
      }),
    ),
  })
  .refine(
    (data) =>
      !data.startDate || !data.endDate || data.endDate >= data.startDate,
    {
      message: "ວັນສິ້ນສຸດຕ້ອງບໍ່ແມ່ນກ່ອນວັນເລີ່ມ",
      path: ["endDate"],
    },
  )
  .refine(
    (data) => {
      const level = data.level ?? "organization";
      if (level === "position") return Boolean(data.positionId?.trim());
      if (level === "department") return Boolean(data.departmentId?.trim());
      return true;
    },
    {
      message: "ກະລຸນາເລືອກຕຳແໜ່ງເມື່ອລະດັບຕຳແໜ່ງ ຫຼື ເລືອກແຜ່ນການເມື່ອລະດັບຜະແນກ",
      path: ["level"],
    },
  );

export type ProjectFormValues = z.infer<typeof ProjectFormSchema>;

/** 5 ชุดข้อมูลสำหรับ dev mode auto fill (สุ่มเลือก 1 ชุดเมื่อกด) */
function getDevFormSamples(): ProjectFormValues[] {
  const today = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  const date = (y: number, m: number, d: number) => `${y}-${pad(m)}-${pad(d)}`;
  const phase = (name: string, dueDate: string) => ({
    id: crypto.randomUUID(),
    name,
    dueDate,
    assigneeId: "",
  });
  return [
    {
      name: "ໂຄງການປັບປຸງລະບົບ ERP",
      description:
        "ຍົກລະດັບລະບົບ ERP ໃຫ້ຮອງຮັບການເຮັດວຽກຫຼາຍສາຂາ ແລະ ລາຍງານ real-time.",
      startDate: date(today.getFullYear(), 1, 15),
      endDate: date(today.getFullYear(), 6, 30),
      responsibleUserId: undefined,
      level: "organization",
      positionId: null,
      departmentId: null,
      phases: [
        phase("ວິເຄາະຄວາມຕ້ອງການ", date(today.getFullYear(), 2, 28)),
        phase("ອອກແບບ ແລະ ພັດທະນາ", date(today.getFullYear(), 4, 15)),
        phase("ທົດສອບ ແລະ ນຳສະເໜີ", date(today.getFullYear(), 6, 15)),
      ],
    },
    {
      name: "ໂຄງການເວັບໄຊທ໌ບໍລິສັດ",
      description: "ເວັບໄຊທ໌ໃໝ່ສຳລັບລູກຄ້າ ແລະ ລະບົບຈັດການຄຳສັ່ງ.",
      startDate: date(today.getFullYear(), 3, 1),
      endDate: date(today.getFullYear(), 8, 31),
      responsibleUserId: undefined,
      level: "organization",
      positionId: null,
      departmentId: null,
      phases: [
        phase("UI/UX Design", date(today.getFullYear(), 4, 1)),
        phase("Frontend development", date(today.getFullYear(), 6, 1)),
        phase("Backend API", date(today.getFullYear(), 7, 15)),
        phase("Integration ແລະ ທົດສອບ", date(today.getFullYear(), 8, 20)),
      ],
    },
    {
      name: "ໂຄງການຝຶກອົບຮົມພະນັກງານ",
      description: "ຝຶກອົບຮົມທັກສະໃໝ່ ແລະ ນະໂຍບາຍບໍລິຫານຄົນທົ່ວທຸກພາກສ່ວນ.",
      startDate: date(today.getFullYear(), 5, 1),
      endDate: date(today.getFullYear(), 9, 30),
      responsibleUserId: undefined,
      level: "organization",
      positionId: null,
      departmentId: null,
      phases: [
        phase("ສ້າງເນື້ອໃນຫຼັກສູດ", date(today.getFullYear(), 6, 1)),
        phase("ຈັດຝຶກອົບຮົມ Round 1", date(today.getFullYear(), 7, 15)),
        phase("ຈັດຝຶກອົບຮົມ Round 2", date(today.getFullYear(), 8, 31)),
        phase("ປະເມີນຜົນ", date(today.getFullYear(), 9, 20)),
      ],
    },
    {
      name: "ໂຄງການວິເຄາະຂໍ້ມູນ ແລະ BI",
      description: "ນຳເຂົ້າຂໍ້ມູນຈາກຫຼາຍແຫຼ່ງ ສ້າງ dashboard ແລະ ລາຍງານສຳລັບບໍລິຫານ.",
      startDate: date(today.getFullYear(), 2, 1),
      endDate: date(today.getFullYear(), 5, 31),
      responsibleUserId: undefined,
      level: "organization",
      positionId: null,
      departmentId: null,
      phases: [
        phase("ເກັບຂໍ້ມູນ ແລະ ETL", date(today.getFullYear(), 3, 15)),
        phase("ອອກແບບ Data model", date(today.getFullYear(), 4, 1)),
        phase("ສ້າງ Dashboard", date(today.getFullYear(), 5, 15)),
      ],
    },
    {
      name: "ໂຄງການປັບປຸງຄວາມປອດໄພຂໍ້ມູນ",
      description: "ປັບປຸງນະໂຍບາຍຄວາມປອດໄພ ເພີ່ມ 2FA ແລະ ກວດສອບສິດເຂົ້າເຖິງ.",
      startDate: date(today.getFullYear(), 4, 1),
      endDate: date(today.getFullYear(), 7, 31),
      responsibleUserId: undefined,
      level: "organization",
      positionId: null,
      departmentId: null,
      phases: [
        phase("ກວດສອບສິດເຂົ້າເຖິງ", date(today.getFullYear(), 4, 30)),
        phase("ເພີ່ມ 2FA ແລະ SSO", date(today.getFullYear(), 6, 15)),
        phase("ຝຶກອົບຮົມຜູ້ໃຊ້", date(today.getFullYear(), 7, 15)),
      ],
    },
  ];
}

export type ProjectFormRef = {
  submit: () => void;
};

type ProjectFormProps = {
  defaultValues: ProjectFormValues;
  onSubmit: (values: ProjectFormValues) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
  mode: "create" | "edit";
  formRef?: React.RefObject<ProjectFormRef | null>;
};

function SelectedProjectLead({ userId }: { userId: string | undefined }) {
  const { data: user } = useQuery({
    queryKey: ["users", "lookup", userId],
    queryFn: () =>
      userId
        ? hydrateLookupItem<UserLookupItem>(
            `${config.apiUrl}/users/lookup`,
            userId,
          )
        : Promise.resolve(null),
    enabled: !!userId,
  });
  if (!userId || !user) return null;
  const src = resolveImageSrc(user.image ?? undefined) ?? undefined;
  return (
    <div className="mt-2 flex items-center gap-3 rounded-lg border bg-muted/30 p-3">
      <Avatar className="h-10 w-10 shrink-0">
        <AvatarImage src={src} alt={user.name} />
        <AvatarFallback>{getInitials(user.name)}</AvatarFallback>
      </Avatar>
      <div className="min-w-0 flex-1">
        <p className="truncate font-medium">{user.name}</p>
        <p className="text-muted-foreground text-xs">ຜູ້ຮັບຜິດຊອບໂຄງການ</p>
      </div>
    </div>
  );
}

export function ProjectForm({
  defaultValues,
  onSubmit,
  onCancel: _onCancel,
  isSubmitting: _isSubmitting,
  mode: _mode,
  formRef,
}: ProjectFormProps) {
  const methods = RHF.useForm<ProjectFormValues>({
    defaultValues: {
      ...defaultValues,
      phases: defaultValues.phases ?? [],
    },
    resolver: zodResolver(ProjectFormSchema),
  });

  React.useEffect(() => {
    if (formRef) {
      formRef.current = {
        submit: () => {
          methods.handleSubmit(
            (data) => {
              const refined = ProjectFormSchema.safeParse(data);
              if (refined.success) onSubmit(refined.data);
              else
                toast.error(
                  refined.error.issues[0]?.message ?? "ກະລຸນາກວດສອບຂໍ້ມູນ",
                );
            },
            () => toast.error("ກະລຸນາກວດສອບຂໍ້ມູນ"),
          )();
        },
      };
    }
  }, [formRef, methods, onSubmit]);

  const { fields, append, remove } = useFieldArray({
    control: methods.control,
    name: "phases",
  });

  const responsibleUserId = RHF.useWatch({
    control: methods.control,
    name: "responsibleUserId",
  });
  const level = RHF.useWatch({ control: methods.control, name: "level" });
  const positionId = RHF.useWatch({
    control: methods.control,
    name: "positionId",
  });
  const departmentId = RHF.useWatch({
    control: methods.control,
    name: "departmentId",
  });

  const userLookupExtraParams: Record<string, string> = (() => {
    const l = level ?? "organization";
    if (l === "position" && positionId?.trim())
      return { positionIds: positionId.trim() } as Record<string, string>;
    if (l === "department" && departmentId?.trim())
      return { departmentId: departmentId.trim() } as Record<string, string>;
    return {};
  })();

  const isDev =
    typeof import.meta !== "undefined" &&
    (import.meta.hot !== undefined || import.meta.env?.MODE === "development");

  const handleAutoFill = () => {
    const samples = getDevFormSamples();
    const sample = samples[Math.floor(Math.random() * samples.length)];
    if (sample) {
      methods.reset(sample);
      toast.success("ເພີ່ມຂໍ້ມູນຕົວຢ່າງແລ້ວ (ສຸ່ມຈາກ 5 ชุด)");
    }
  };

  const addPhase = () => {
    append({
      id: crypto.randomUUID(),
      name: "",
      dueDate: "",
      assigneeId: "",
    });
  };

  return (
    <FormRoot<ProjectFormValues>
      methods={methods}
      onSubmit={(vals) => {
        const refined = ProjectFormSchema.safeParse(vals);
        if (!refined.success) {
          toast.error(refined.error.issues[0]?.message ?? "ກະລຸນາກວດສອບຂໍ້ມູນ");
          return;
        }
        onSubmit(refined.data);
      }}
      className="space-y-6"
    >
      {isDev && (
        <div className="flex justify-end">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleAutoFill}
            className="gap-1.5 text-muted-foreground"
          >
            <Sparkles className="h-4 w-4" />
            Auto fill (dev)
          </Button>
        </div>
      )}
      <div className="grid gap-6 lg:grid-cols-[1fr_340px]">
        {/* Left column: Core Identification + Phases */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">ຂໍ້ມູນຫຼັກ</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div data-tourid="form-name">
                <FormInput
                  name="name"
                  label="ຊື່ໂຄງການ"
                  placeholder="ຕົວຢ່າງ: ໂຄງການປັບປຸງລະບົບ ERP"
                  requiredMark
                />
              </div>
              <div data-tourid="form-description">
                <FormTextarea
                  name="description"
                  label="ຄຳອະທິບາຍ"
                  placeholder="ອະທິບາຍເປົ້າໝາຍ ແລະ ຂອບເຂດໂຄງການ..."
                  rows={3}
                  className="resize-none"
                />
              </div>
            </CardContent>
          </Card>

          <Card data-tourid="form-phases">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-base">ຂັ້ນຕອນ ແລະ ຈຸດໝາຍ</CardTitle>
              <Button
                type="button"
                variant="default"
                size="sm"
                onClick={addPhase}
                className="gap-1"
              >
                <PlusIcon className="h-4 w-4" />
                ເພີ່ມຂັ້ນຕອນ
              </Button>
            </CardHeader>
            <CardContent className="space-y-3">
              {fields.map((field, index) => (
                <div
                  key={field.id}
                  className="flex items-start gap-2 rounded-xl border bg-card p-3"
                >
                  <button
                    type="button"
                    className="mt-2 shrink-0 cursor-grab touch-none text-muted-foreground hover:text-foreground"
                    aria-label="ລາກເພື່ອຈັດລຳດັບ"
                    onMouseDown={(e) => e.preventDefault()}
                  >
                    <GripVertical className="h-4 w-4" />
                  </button>
                  <div className="min-w-0 flex-1 space-y-2">
                    <Field name={`phases.${index}.name`} label="" requiredMark>
                      <RHF.Controller
                        control={methods.control}
                        name={`phases.${index}.name`}
                        render={({ field: f }) => (
                          <input
                            {...f}
                            placeholder="ຊື່ຂັ້ນຕອນ"
                            className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                          />
                        )}
                      />
                    </Field>
                    <div className="grid grid-cols-2 gap-2">
                      <FormInput
                        name={`phases.${index}.dueDate`}
                        label="ວັນທີໝາຍ"
                        placeholder="ວັນທີໝາຍ"
                        type="date"
                        requiredMark
                      />
                      <FormInfiniteCombobox<UserLookupItem>
                        name={`phases.${index}.assigneeId`}
                        label="ມອບໝາຍ"
                        queryKey={[
                          "users",
                          "project-phase",
                          String(index),
                          level ?? "",
                          positionId ?? "",
                          departmentId ?? "",
                        ]}
                        queryFn={(args) =>
                          fetchLookupForInfinite(
                            `${config.apiUrl}/users/lookup`,
                            args,
                            Object.keys(userLookupExtraParams).length > 0
                              ? userLookupExtraParams
                              : undefined,
                          )
                        }
                        preloadQueryFn={(id) =>
                          hydrateLookupItem<UserLookupItem>(
                            `${config.apiUrl}/users/lookup`,
                            id,
                          )
                        }
                        getLabel={(item) => item.name}
                        getValue={(item) => item.id}
                        placeholder="ເລືອກຜູ້ຮັບຜິດຊອບຂັ້ນຕອນ..."
                        clearable
                      />
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="shrink-0 text-muted-foreground hover:text-destructive"
                    onClick={() => remove(index)}
                    aria-label="ລຶບຂັ້ນຕອນ"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              <button
                type="button"
                onClick={addPhase}
                className="flex w-full items-center justify-center gap-2 rounded-xl border border-muted-foreground/40 border-dashed bg-muted/20 py-4 text-muted-foreground text-sm transition-colors hover:border-muted-foreground hover:bg-muted/40"
              >
                <PlusIcon className="h-4 w-4" />
                ເພີ່ມຂັ້ນຕອນອື່ນ...
              </button>
            </CardContent>
          </Card>
        </div>

        {/* Right column: Project Window + Project Lead */}
        <div className="space-y-6">
          <Card data-tourid="form-dates">
            <CardHeader>
              <CardTitle className="text-base">ຊ່ອງເວລາໂຄງການ</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Field name="startDate" label="ວັນເລີ່ມ" requiredMark>
                <RHF.Controller
                  control={methods.control}
                  name="startDate"
                  render={({ field, fieldState }) => (
                    <div>
                      <div className="relative flex items-center">
                        <CalendarIcon className="absolute left-3 h-4 w-4 text-muted-foreground" />
                        <input
                          type="date"
                          value={field.value ?? ""}
                          onChange={(e) => field.onChange(e.target.value || "")}
                          className="flex h-10 w-full rounded-md border border-input bg-background py-2 pr-3 pl-9 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        />
                      </div>
                      {fieldState.error && (
                        <p className="mt-1 text-destructive text-xs">
                          {fieldState.error.message}
                        </p>
                      )}
                    </div>
                  )}
                />
              </Field>
              <Field name="endDate" label="ວັນສິ້ນສຸດ" requiredMark>
                <RHF.Controller
                  control={methods.control}
                  name="endDate"
                  render={({ field, fieldState }) => (
                    <div>
                      <div className="relative flex items-center">
                        <CalendarIcon className="absolute left-3 h-4 w-4 text-muted-foreground" />
                        <input
                          type="date"
                          value={field.value ?? ""}
                          onChange={(e) => field.onChange(e.target.value || "")}
                          className="flex h-10 w-full rounded-md border border-input bg-background py-2 pr-3 pl-9 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        />
                      </div>
                      {fieldState.error && (
                        <p className="mt-1 text-destructive text-xs">
                          {fieldState.error.message}
                        </p>
                      )}
                    </div>
                  )}
                />
              </Field>
            </CardContent>
          </Card>

          <Card data-tourid="form-level">
            <CardHeader>
              <CardTitle className="text-base">ລະດັບໂຄງການ</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Field name="level" label="ລະດັບ">
                <RHF.Controller
                  control={methods.control}
                  name="level"
                  render={({ field }) => (
                    <RadioGroup
                      value={field.value ?? "organization"}
                      onValueChange={(v: string) => {
                        field.onChange(v);
                        if (v !== "position")
                          methods.setValue("positionId", null);
                        if (v !== "department")
                          methods.setValue("departmentId", null);
                      }}
                      className="grid grid-cols-1 gap-2 sm:grid-cols-3"
                      aria-label="ລະດັບໂຄງການ"
                    >
                      {LEVEL_OPTIONS.map((opt) => (
                        <label
                          key={opt.value}
                          htmlFor={`project-level-${opt.value}`}
                          className={cn(
                            "flex cursor-pointer flex-col items-center justify-center gap-1.5 rounded-lg border-2 px-4 py-3 transition-colors",
                            (field.value ?? "organization") === opt.value
                              ? "border-primary bg-primary/10"
                              : "border-border hover:border-muted-foreground/50 hover:bg-muted/50",
                          )}
                        >
                          <RadioGroupItem
                            value={opt.value}
                            id={`project-level-${opt.value}`}
                            className="sr-only"
                          />
                          <span className="font-medium text-sm">
                            {opt.label}
                          </span>
                        </label>
                      ))}
                    </RadioGroup>
                  )}
                />
              </Field>
              {(level ?? "organization") === "position" && (
                <Field name="positionId" label="ຕຳແໜ່ງ" requiredMark>
                  <RHF.Controller
                    control={methods.control}
                    name="positionId"
                    render={({ field: posField }) => (
                      <InfiniteCombobox<LookupItem>
                        queryKey={["positions", "lookup", "project-form"]}
                        queryFn={(args) =>
                          fetchLookupForInfinite(
                            `${config.apiUrl}/positions/lookup`,
                            args,
                          )
                        }
                        preloadQueryFn={(id) =>
                          hydrateLookupItem(
                            `${config.apiUrl}/positions/lookup`,
                            id,
                          )
                        }
                        getLabel={(item) => item.name}
                        getValue={(item) => item.id}
                        value={posField.value ?? ""}
                        onValueChange={(v) =>
                          posField.onChange(v === "" ? null : v)
                        }
                        placeholder="ເລືອກຕຳແໜ່ງ..."
                        clearable
                      />
                    )}
                  />
                </Field>
              )}
              {(level ?? "organization") === "department" && (
                <Field name="departmentId" label="ແຜ່ນການ" requiredMark>
                  <RHF.Controller
                    control={methods.control}
                    name="departmentId"
                    render={({ field: deptField }) => (
                      <InfiniteCombobox<LookupItem>
                        queryKey={["departments", "lookup", "project-form"]}
                        queryFn={(args) =>
                          fetchLookupForInfinite(
                            `${config.apiUrl}/departments/lookup`,
                            args,
                          )
                        }
                        preloadQueryFn={(id) =>
                          hydrateLookupItem(
                            `${config.apiUrl}/departments/lookup`,
                            id,
                          )
                        }
                        getLabel={(item) => item.name}
                        getValue={(item) => item.id}
                        value={deptField.value ?? ""}
                        onValueChange={(v) =>
                          deptField.onChange(v === "" ? null : v)
                        }
                        placeholder="ເລືອກແຜ່ນການ..."
                        clearable
                      />
                    )}
                  />
                </Field>
              )}
            </CardContent>
          </Card>

          <Card data-tourid="form-responsible">
            <CardHeader>
              <CardTitle className="text-base">ຜູ້ຮັບຜິດຊອບໂຄງການ</CardTitle>
            </CardHeader>
            <CardContent>
              <FormInfiniteCombobox<UserLookupItem>
                name="responsibleUserId"
                label=""
                queryKey={[
                  "users",
                  "project-form",
                  level ?? "",
                  positionId ?? "",
                  departmentId ?? "",
                ]}
                queryFn={(args) =>
                  fetchLookupForInfinite(
                    `${config.apiUrl}/users/lookup`,
                    args,
                    Object.keys(userLookupExtraParams).length > 0
                      ? userLookupExtraParams
                      : undefined,
                  )
                }
                preloadQueryFn={(id) =>
                  hydrateLookupItem(`${config.apiUrl}/users/lookup`, id)
                }
                getLabel={(item) => item.name}
                getValue={(item) => item.id}
                placeholder="ເລືອກຜູ້ຮັບຜິດຊອບ..."
                clearable
              />
              <SelectedProjectLead userId={responsibleUserId} />
            </CardContent>
          </Card>
        </div>
      </div>
    </FormRoot>
  );
}
